import React, { useEffect, useState } from "react";
import { TableCell, TableHead, TableRow, TableContainer, Table, TableBody, Toolbar, Typography, Switch } from "@mui/material";
import { Done, Edit, Undo } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AllowedFileExtensions, AutomaticApprovalSettings, DataType, UserRoles } from "../../../Constant";
import AutomaticApprovalApiService from "../../../adapters/xhr/AutomaticApprovalApiService";
import authService from "../../../components/api-authorization/AuthorizeService";
import { MyNumberField } from "../../../components/my-number-field/MyNumberField";
import { MyTextField } from "../../../components/my-text-field/MyTextField";
import { MySelect } from "../../../components/my-select/MySelect";
import SnackbarUtils from "../../../SnackbarUtils";
import { getAutomaticApprovalEnabled, isValidAutomaticApprovalValue, mapAutomaticApprovalRows } from "./AutomaticApprovalSettingsHelper";

export const AutoClaimProcessingSection = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [previousRows, setPreviousRows] = useState([]);
  const [autoApproval, setAutoApproval] = useState(true);
  const isReadOnly = authService._user?.role == UserRoles.CaseManager;
  const hasEditRow = rows.some((item) => item.isEditMode);

  const columns = [
    { id: "Title", label: t("title"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    { id: "Actions", label: t("actions"), minWidth: 170, hidden: isReadOnly },
  ];

  useEffect(() => {
    fetchSettingsAsync();
  }, []);

  const fetchSettingsAsync = async () => {
    const response = await AutomaticApprovalApiService.getAll();
    if (response?.isSuccess) {
      setAutoApproval(getAutomaticApprovalEnabled(response.data));
      const data = mapAutomaticApprovalRows(response.data, t);
      setRows(data);
      setPreviousRows(data);
    }
  };

  const filteredRows =
    rows?.filter(
      (item) =>
        !item.Key.startsWith(AutomaticApprovalSettings.LinesToRemove) &&
        !item.Key.startsWith(AutomaticApprovalSettings.StopsToRemove) &&
        !item.Key.startsWith(AutomaticApprovalSettings.Periods) &&
        [
          AutomaticApprovalSettings.MaxClaimsIn24Hours,
          AutomaticApprovalSettings.MaxClaimsDuring7Days,
          AutomaticApprovalSettings.MaxClaimsDuring30Days,
        ].indexOf(item.Key) <= -1
    ) ?? [];

  const onEditRow = (id) => {
    setRows(
      previousRows.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: true };
        }

        return row;
      })
    );
  };

  const onChangeRowValues = (value, name, row) => {
    if (name === "Value" && !isValidAutomaticApprovalValue(row, value)) {
      return;
    }

    setRows(
      rows.map((item) => {
        if (item.id === row.id) {
          return { ...item, [name]: value };
        }

        return item;
      })
    );
  };

  const onRevertRows = () => {
    setRows(previousRows);
  };

  const onToggleAutoApproval = async (checked) => {
    const response = await AutomaticApprovalApiService.update(AutomaticApprovalSettings.Enable, { key: AutomaticApprovalSettings.Enable, value: checked.toString() });
    
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage ?? "error"));
      return;
    }

    setAutoApproval(checked);
  };

  const onSaveSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);
    
    if (!row.Value) return;

    const payload = { key: row.Key, value: `${row.Value}` };
    const response = await AutomaticApprovalApiService.update(row.Key, payload);
    
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage ?? "error"));
      onRevertRows();
      return;
    }

    await fetchSettingsAsync();
  };

  return (
    <>
      <Toolbar>
        <Typography variant="h6" id="tableTitle" component="div">
          {t("auto_claim_processing")}
        </Typography>
        <Switch className="margin left-1 top-0 bottom-0" color="primary" disabled={isReadOnly} checked={autoApproval} onChange={(e, checked) => onToggleAutoApproval(checked)} />
      </Toolbar>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns
                .filter((item) => !item.hidden)
                .map((column, key) => (
                  <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                    {column.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row, key) => {
              return (
                <TableRow hover tabIndex={-1} key={key}>
                  {columns
                    .filter((item) => !item.hidden)
                    .map((column) => {
                      if (column.id === "Actions") {
                        return (
                          <TableCell key={100} align="left">
                            {!row.isEditMode && !hasEditRow && <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onEditRow(row.id)} />}
                            {row.isEditMode && (
                              <>
                                <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveSettingsAsync(row.id)} />
                                <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertRows()} />
                              </>
                            )}
                          </TableCell>
                        );
                      }

                      let value = row[column.id];

                      if ((row.isEditMode || row.isAddMode) && column.editable) {
                        if (row.DataType === DataType.Bool) {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <MyTextField size="small" value={value} onChange={(nextValue) => onChangeRowValues(nextValue, column.id, row)} />
                            </TableCell>
                          );
                        }

                        if (row.Key == AutomaticApprovalSettings.AllowedFileExtension) {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <MySelect
                                size="small"
                                style={{ width: "15rem" }}
                                label={t("status")}
                                onChange={(values) => onChangeRowValues(values.map((item) => item.value).toString(), column.id, row)}
                                options={AllowedFileExtensions}
                                value={value.split(",").map((item) => ({ key: item, value: item }))}
                              />
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={column.id} align={column.align}>
                            <MyNumberField
                              decimalSeparator={row.DataType === DataType.Decimal ? "," : false}
                              size="small"
                              value={value.replace(".", ",")}
                              onChange={(e) => onChangeRowValues(e.target.value.replace(",", "."), column.id, row)}
                            />
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {value.replace(".", ",")}
                        </TableCell>
                      );
                    })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};